import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFlipFixedList } from '../components/useFlipFixedList';
import ReactDOM from 'react-dom';
import { useScrollSnap } from '../hooks/useScrollSnap';
import {
  IonAlert,
  IonToast,
  IonBackdrop,
  IonBadge,
  IonButton,
  IonCard,
  IonCardContent,
  IonCheckbox,
  IonContent,
  IonGrid,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonSpinner,
} from '@ionic/react';
import {
  addOutline,
  callOutline,
  chevronBack,
  chevronDownOutline,
  chevronForwardOutline,
  ellipsisHorizontal,
  keyOutline,
  mailOutline,
  notificationsOutline,
  peopleOutline,
  refreshOutline,
  shieldCheckmarkOutline,
  trashOutline,
} from 'ionicons/icons';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../contexts';
import { dbRsRoles } from '../auth/firebase';
import { rsAuth } from '../services/rsAuthClient';
import { VERIFICATION_CONFIG } from '@rsmaids/shared/constants/auth';
import { canAssignRole, canManageAccount } from '@rsmaids/shared/constants/roles';
import { StaffCacheService, type CachedStaffMember } from '@rsmaids/shared/services/api/staffCacheService';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { getWixBusinessProfile } from '@rsmaids/shared/services/api/businessInfo';
import GlassConfirmSheet from '../components/GlassConfirmSheet';
import SheetModal from '../components/SheetModal';
import BasePageWithHeader, { TitleCard } from '../components/BasePageWithHeader';
import styles from './EmployeeManagement.module.scss';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { translateDynamic } from '../i18n/dynamic';

interface EmployeeManagementProps {
  onClose?: () => void;
  // Employee Management no longer owns Phone Agents itself — it links out to
  // the Phone Agents modal Manage.tsx already owns, instead of duplicating
  // that feature (agent list, add/remove, availability toggling) here too.
  onOpenPhoneAgents?: () => void;
}

type SectionKey = 'invite' | 'codes' | 'notifications' | 'roles' | 'addStaff' | 'resources';

interface EmployeeCode {
  code: string;
  email: string;
  role: string;
  createdAt: Date;
  expiresAt: Date;
}

interface NotificationCategories {
  bookings: boolean;
  payments: boolean;
  team: boolean;
  forms: boolean;
  overdue: boolean;
  invoice: boolean;
}

interface EmployeeRecord {
  id: string;
  staffId: string;
  name: string;
  email: string;
  role: string;
  notificationsEnabled: boolean;
  notificationCategories?: NotificationCategories;
}

interface ResourceRecord {
  _id: string;
  name: string;
  description?: string;
  type?: string;
  revision?: string;
  // Real fields from Wix — its own dashboard shows these as the "Location"
  // and "Availability" columns (there's no real "description" on a Wix
  // Resource at all, so that field is basically always empty in practice).
  locationOptions?: {
    availableInAllLocations?: boolean;
    specificLocationOptions?: {
      availableInBusinessLocations?: boolean;
      businessLocations?: { locationId: string }[];
    };
  };
  workingHoursSchedules?: { values?: { scheduleId?: string }[] };
}

const resourceLocationIds = (r: ResourceRecord): string[] =>
  (r.locationOptions?.specificLocationOptions?.businessLocations ?? [])
    .map((b) => b.locationId)
    .filter(Boolean);

// Matches Wix's own "Create Resource" dialog exactly: a resource is either
// unrestricted 24/7 (no working-hours schedule at all) or follows the
// business's own default schedule — not a bespoke per-resource hour-by-hour
// editor, which Wix's own UI doesn't even offer at this level.
const resourceIs247 = (r: ResourceRecord) => !r.workingHoursSchedules?.values?.length;

// Wix groups resources under named categories (its dashboard calls these
// "categories" — e.g. "Team Member 01") via a separate ResourceType record;
// resources.getResourcesGroupedByCategory() already does the join, we just
// need to render its shape instead of a flat list.
type GroupedResources = Record<string, ResourceRecord[]>;

const roleOptions = Object.entries(VERIFICATION_CONFIG.ASSIGNABLE_ROLES).map(([label, value]) => ({
  label: label.charAt(0) + label.slice(1).toLowerCase().replace('_', ' '),
  value,
}));

const getRoleTone = (role: string) => {
  const key = (role || '').toLowerCase();
  if (key.includes('owner') || key.includes('admin')) return styles.roleDanger;
  if (key.includes('manager')) return styles.rolePrimary;
  if (key.includes('lead')) return styles.roleWarning;
  return styles.roleMedium;
};

const formatExpiry = (expiresAt: Date) => {
  const diffMs = expiresAt.getTime() - Date.now();
  if (diffMs <= 0) return i18n.t('pages.EmployeeManagement.expired');
  const totalMins = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  return hours > 0 ? `${hours}h ${mins}m remaining` : `${mins}m remaining`;
};

const getStaffName = (staff: CachedStaffMember): string => {
  const name = (staff as unknown as { name?: string }).name;
  const first = (staff as unknown as { firstName?: string }).firstName;
  const last = (staff as unknown as { lastName?: string }).lastName;
  const email = (staff as unknown as { email?: string }).email;
  return name || [first, last].filter(Boolean).join(' ').trim() || email || i18n.t('pages.EmployeeManagement.staffMember');
};

// ─── Generic dropdown — same portal + position pattern used for every other
// custom picker in this app (e.g. CreateInvoice.tsx's PickerDropdown),
// styled with the glass dropdown chrome instead of a native <select>. The
// floating list portals into an IonCard (not a <div>) to match that same
// established "floating card" convention. Used for both Role and Location
// selects — an inline dropdown, not a full-screen modal. ───────────────────
interface SimpleSelectOption {
  value: string;
  label: string;
}
interface SimpleSelectProps {
  value: string;
  onChange: (v: string) => void;
  options: SimpleSelectOption[];
  className?: string;
  placeholder?: string;
  cancelLabel?: string;
  onCancel?: () => void;
}
const SimpleSelect: React.FC<SimpleSelectProps> = ({ value, onChange, options, className, placeholder, cancelLabel, onCancel }) => {
  const [open, setOpen] = useState(false);
  const [listPos, setListPos] = useState({ top: 0, left: 0, width: 0 });
  const wrapperRef = useRef<HTMLIonLabelElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLIonCardElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const inTrigger = !!wrapperRef.current?.contains(target);
      const inList = !!listRef.current?.contains(target);
      if (!inTrigger && !inList) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useFlipFixedList(open, triggerRef, listRef);

  const handleOpen = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setListPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setOpen(v => !v);
  };

  const selected = options.find(r => r.value === value);
  const displayLabel = selected?.label || placeholder || value;
  const isPlaceholder = !selected && !!placeholder;

  return (
    <IonLabel ref={wrapperRef} className={`${styles.fieldBox} ${styles.roleSelectWrap} ${className || ''}`}>
      <button
        type="button"
        ref={triggerRef}
        className={styles.roleSelectTrigger}
        onClick={handleOpen}
      >
        <span className={`${styles.fieldInput} ${isPlaceholder ? styles.fieldInputPlaceholder : ''}`}>{displayLabel}</span>
        <IonIcon icon={chevronDownOutline} className={`${styles.roleSelectChevron} ${open ? styles.roleSelectChevronOpen : ''}`} />
      </button>
      {open && ReactDOM.createPortal(
        <>
        <IonBackdrop tappable className={styles.dropdownBackdrop} onIonBackdropTap={() => setOpen(false)} />
        <IonCard ref={listRef} className={styles.roleDropdownList} style={{ top: listPos.top, left: listPos.left, minWidth: listPos.width }}>
          {options.map(opt => (
            <button
              key={opt.value}
              type="button"
              className={`${styles.roleDropdownItem} ${opt.value === value ? styles.roleDropdownItemActive : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onChange(opt.value); setOpen(false); }}
            >
              {opt.label}
            </button>
          ))}
          {cancelLabel && (
            <button
              type="button"
              className={`${styles.roleDropdownItem} ${styles.roleDropdownCancel}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onCancel?.(); setOpen(false); }}
            >
              {cancelLabel}
            </button>
          )}
        </IonCard>
        </>,
        document.body
      )}
    </IonLabel>
  );
};

// ─── "Manage Category" menu — same portal + position pattern as RoleSelect,
// matching Wix's own dashboard's per-category dropdown (Add Resource /
// Rename / Delete). ─────────────────────────────────────────────────────
interface CategoryMenuProps {
  onAddResource: () => void;
  onRename: () => void;
  onDelete: () => void;
}
const CategoryMenu: React.FC<CategoryMenuProps> = ({ onAddResource, onRename, onDelete }) => {
  const { t: tr } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLIonCardElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!triggerRef.current?.contains(target) && !listRef.current?.contains(target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useFlipFixedList(open, triggerRef, listRef);

  const handleOpen = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, right: Math.max(8, window.innerWidth - rect.right) });
    }
    setOpen((v) => !v);
  };

  return (
    <>
      <button type="button" ref={triggerRef} className={styles.categoryMenuTrigger} onClick={handleOpen}>
        <IonIcon icon={ellipsisHorizontal} />
      </button>
      {open && ReactDOM.createPortal(
        <>
        <IonBackdrop tappable className={styles.dropdownBackdrop} onIonBackdropTap={() => setOpen(false)} />
        <IonCard ref={listRef} className={styles.categoryMenuList} style={{ top: pos.top, right: pos.right }}>
          <button type="button" className={styles.categoryMenuItem} onClick={() => { setOpen(false); onAddResource(); }}>{tr('pages.EmployeeManagement.addResource')}
          </button>
          <button type="button" className={styles.categoryMenuItem} onClick={() => { setOpen(false); onRename(); }}>{tr('pages.EmployeeManagement.rename')}
          </button>
          <button type="button" className={`${styles.categoryMenuItem} ${styles.categoryMenuItemDanger}`} onClick={() => { setOpen(false); onDelete(); }}>{tr('pages.EmployeeManagement.delete')}
          </button>
        </IonCard>
        </>,
        document.body
      )}
    </>
  );
};

// i18n-ignore: CSS class names, not text
const TOAST_CLASS_OK = 'app-toast';
// i18n-ignore: CSS class names, not text
const TOAST_CLASS_ERROR = 'app-toast app-toast--error';

const EmployeeManagement: React.FC<EmployeeManagementProps> = ({ onClose, onOpenPhoneAgents }) => {
  const { t: tr } = useTranslation();
  const { authState } = useAuth();
  const user = authState.user;
  // Offer only the roles this person is allowed to hand out (the server enforces the same rule)
  const assignableRoleOptions = roleOptions.filter(o => canAssignRole(user?.role, o.value));
  const functions = getFunctions();

  // Refs for the collapsing-title-on-scroll animation — same pattern as
  // Manage.tsx/BusinessProfile.tsx.
  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
  const containerRef = useRef<HTMLIonGridElement>(null);

  const applyProgress = useCallback((progress: number, _smooth: boolean) => {
    baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
    if (titleCardAnimationRef.current) {
      titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  const handleScroll = useScrollSnap(containerRef, 66, applyProgress);

  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<EmployeeRecord[]>([]);
  const [codes, setCodes] = useState<EmployeeCode[]>([]);
  const [resources, setResources] = useState<GroupedResources>({});
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState(''); // why the server refused the invitation (e.g. the email already has an account)
  const [inviteSuccess, setInviteSuccess] = useState(''); // also used for the "staff member deleted" message: both show in the same toast
  const [isDeletingStaff, setIsDeletingStaff] = useState(false);
  const [newStaffFirstName, setNewStaffFirstName] = useState('');
  const [newStaffLastName, setNewStaffLastName] = useState('');
  const [newStaffEmail, setNewStaffEmail] = useState('');
  const [newStaffPhone, setNewStaffPhone] = useState('');
  const [staffExistsInlineMessage, setStaffExistsInlineMessage] = useState('');
  const [isSavingStaff, setIsSavingStaff] = useState(false);
  const [newResourceName, setNewResourceName] = useState('');
  const [isSavingResource, setIsSavingResource] = useState(false);
  const [targetCategory, setTargetCategory] = useState<{ id: string; name: string } | null>(null);
  const [resourceTypes, setResourceTypes] = useState<{ _id: string; name: string; revision?: string }[]>([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [renamingCategory, setRenamingCategory] = useState<{ id: string; name: string } | null>(null);
  const [renameCategoryValue, setRenameCategoryValue] = useState('');
  const [isSavingCategoryRename, setIsSavingCategoryRename] = useState(false);
  const [deleteCategory, setDeleteCategory] = useState<{ id: string; name: string } | null>(null);
  // The business's own default schedule — a resource's "Default business
  // hours" availability option just points its workingHoursSchedules at this
  // same schedule (shared:true), matching Wix's own "Create Resource" dialog
  // exactly (a plain 24/7 vs. Default-business-hours toggle, not a bespoke
  // per-resource hour editor).
  const businessScheduleIdRef = useRef<string | null>(null);
  const [savingAvailabilityFor, setSavingAvailabilityFor] = useState<string | null>(null);
  // Matches Wix's own "Create Resource" dialog: Location is a simple
  // single-select against the business's real locations, not a multi-select
  // picker — fetched once and cached, same lazy-once pattern as scheduleId.
  const businessLocationsFetchedRef = useRef(false);
  const [businessLocations, setBusinessLocations] = useState<{ _id: string; name: string }[]>([]);
  const [roleEdits, setRoleEdits] = useState<Record<string, string>>({});
  const [savingRoleId, setSavingRoleId] = useState<string | null>(null);
  const [deleteStaff, setDeleteStaff] = useState<EmployeeRecord | null>(null);
  const [deleteResource, setDeleteResource] = useState<ResourceRecord | null>(null);
  const [deleteCode, setDeleteCode] = useState<string | null>(null);
  const [panelLoading, setPanelLoading] = useState(false);
  const [categoriesEmployee, setCategoriesEmployee] = useState<EmployeeRecord | null>(null);
  const [draftCategories, setDraftCategories] = useState<NotificationCategories>({ bookings: true, payments: true, team: true, forms: true, overdue: true, invoice: true });
  const [, setSavingCategories] = useState(false);
  const forceStaffReloadRef = useRef(false);
  const [collapsedSections, setCollapsedSections] = useState<Record<SectionKey, boolean>>({
    invite: false,
    codes: true,
    notifications: true,
    roles: true,
    addStaff: true,
    resources: true,
  });

  const listRoleCodes = useCallback(async () => {
    const res = await rsAuth.listRoleCodes();
    if (!res.success || !res.codes) return [];
    return res.codes.map((code: EmployeeCode) => ({
      ...code,
      createdAt: new Date(code.createdAt),
      expiresAt: new Date(code.expiresAt),
    }));
  }, []);

  const listEmployees = useCallback(async () => {
    const staffCache = StaffCacheService.getInstance();
    const forceReload = forceStaffReloadRef.current;
    forceStaffReloadRef.current = false;
    await staffCache.loadStaffCache(forceReload);
    const staff = staffCache.getAllStaff();
    const getStatus = httpsCallable(functions, 'getEmployeeNotificationStatus');

    const currentUserContactId = user?.wixContactId || '';

    const filteredStaff = staff.filter((member) => {
      const rawMember = member as unknown as {
        email?: string;
        contactId?: string;
        associatedWixIdentity?: { identificationData?: { contactId?: string } };
      };
      const memberContactId = rawMember.contactId
        || rawMember.associatedWixIdentity?.identificationData?.contactId
        || '';
      if (currentUserContactId && memberContactId === currentUserContactId) return false;
      return true;
    });

    const mapped = await Promise.all(filteredStaff.map(async (member) => {
      const m = member as unknown as { id?: string; staffMemberId?: string; _id?: string; email?: string; role?: string };
      const email = m.email || '';
      let notificationsEnabled = false;
      let resolvedRole = m.role || 'staff';

      if (email) {
        try {
          const result = await getStatus({ email });
          const data = result.data as { notificationsEnabled?: boolean; role?: string; preferences?: { categories?: NotificationCategories } };
          if (typeof data.notificationsEnabled === 'boolean') notificationsEnabled = data.notificationsEnabled;
          if (data.role && typeof data.role === 'string') resolvedRole = data.role;
          if (data.preferences?.categories) {
            (member as unknown as Record<string, unknown>)._notifCategories = data.preferences.categories;
          }
        } catch {
          notificationsEnabled = false;
        }
      }

      return {
        id: m.staffMemberId || m.id || m._id || email,
        staffId: m.staffMemberId || m.id || m._id || '',
        name: getStaffName(member),
        email,
        role: resolvedRole,
        notificationsEnabled,
        notificationCategories: (member as unknown as Record<string, unknown>)._notifCategories as NotificationCategories | undefined,
      } as EmployeeRecord;
    }));

    return mapped.sort((a, b) => a.name.localeCompare(b.name));
  }, [functions, user?.wixContactId]);

  const listResources = useCallback(async () => {
    const service = DataServiceFactory.getService();
    const grouped = await service.resources.getResourcesGroupedByCategory();
    return grouped as unknown as GroupedResources;
  }, []);

  const listResourceTypes = useCallback(async () => {
    const service = DataServiceFactory.getService();
    return await service.resources.getAllResourceTypes() as { _id: string; name: string; revision?: string }[];
  }, []);

  const syncStaffCache = useCallback(async (): Promise<boolean> => {
    try {
      const syncStaffCacheFunction = httpsCallable<void, { success: boolean; message: string; count?: number }>(
        functions,
        'syncStaffCacheEndpoint'
      );
      await syncStaffCacheFunction();

      forceStaffReloadRef.current = true;
      const freshEmployees = await listEmployees();
      setEmployees(freshEmployees);
      setRoleEdits(Object.fromEntries(freshEmployees.map((e) => [e.id, e.role])));
      return true;
    } catch (error) {
      console.error('Error syncing staff cache:', error);
      return false;
    }
  }, [functions, listEmployees]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      forceStaffReloadRef.current = true;
      const [freshCodes, freshEmployees] = await Promise.all([
        listRoleCodes(),
        listEmployees(),
      ]);
      const [freshResources, freshResourceTypes] = await Promise.all([
        listResources(),
        listResourceTypes(),
      ]);
      setCodes(freshCodes);
      setEmployees(freshEmployees);
      setResources(freshResources);
      setResourceTypes(freshResourceTypes);
      setRoleEdits(Object.fromEntries(freshEmployees.map((e) => [e.id, e.role])));
      if (!businessScheduleIdRef.current) {
        getWixBusinessProfile()
          .then((profile) => { businessScheduleIdRef.current = profile._scheduleId ?? null; })
          .catch(() => {});
      }
      if (!businessLocationsFetchedRef.current) {
        businessLocationsFetchedRef.current = true;
        DataServiceFactory.getService().resources.getBusinessLocations()
          .then(setBusinessLocations)
          .catch(() => {});
      }
    } finally {
      setLoading(false);
    }
  }, [listRoleCodes, listEmployees, listResources, listResourceTypes]);

  const refreshEmployeesSilently = useCallback(async () => {
    forceStaffReloadRef.current = true;
    const freshEmployees = await listEmployees();
    setEmployees(freshEmployees);
    setRoleEdits(Object.fromEntries(freshEmployees.map((e) => [e.id, e.role])));
  }, [listEmployees]);

  useEffect(() => {
    Promise.resolve().then(() => refreshAll());
  }, [refreshAll]);

  useEffect(() => {
    if (!user) return;

    let inFlight = false;
    let queued = false;

    const queueSilentRefresh = () => {
      if (inFlight) { queued = true; return; }
      inFlight = true;
      void (async () => {
        try {
          await refreshEmployeesSilently();
        } finally {
          inFlight = false;
          if (queued) { queued = false; queueSilentRefresh(); }
        }
      })();
    };

    const unsubscribeStaff = onSnapshot(
      collection(dbRsRoles, 'staff'),
      queueSilentRefresh,
      (error) => console.warn('Silent staff refresh listener error:', error)
    );

    return () => unsubscribeStaff();
  }, [user, refreshEmployeesSilently]);

  const toggleSection = (key: SectionKey) => {
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCreateInvite = async () => {
    if (!inviteEmail.trim() || !inviteRole) return;
    setIsInviting(true);
    try {
      const invitedEmail = inviteEmail.trim();
      const result = await rsAuth.createEmployeeCode(invitedEmail, inviteRole);
      if (!result.success) setInviteError((typeof result.error === 'string' ? result.error : result.error?.message) || tr('pages.EmployeeManagement.couldNotSendTheInvitation'));
      if (result.success) {
        setInviteSuccess(tr('pages.EmployeeManagement.invitationSentTo', { invitedEmail }));
        setInviteEmail('');
        setInviteRole('');
        const freshCodes = await listRoleCodes();
        setCodes(freshCodes);
      }
    } finally {
      setIsInviting(false);
    }
  };

  const openCategoriesModal = (employee: EmployeeRecord) => {
    setCategoriesEmployee(employee);
    setDraftCategories({
      bookings: employee.notificationCategories?.bookings ?? true,
      payments: employee.notificationCategories?.payments ?? true,
      team: employee.notificationCategories?.team ?? true,
      forms: employee.notificationCategories?.forms ?? true,
      overdue: employee.notificationCategories?.overdue ?? true,
      invoice: employee.notificationCategories?.invoice ?? true,
    });
  };

  const handleSaveCategories = async () => {
    if (!categoriesEmployee?.email) return;
    setSavingCategories(true);
    try {
      const fn = httpsCallable(functions, 'updateEmployeeNotificationCategories');
      await fn({ email: categoriesEmployee.email, categories: draftCategories });
      setEmployees((prev) => prev.map((e) =>
        e.id === categoriesEmployee.id ? { ...e, notificationCategories: { ...draftCategories } } : e
      ));
      setCategoriesEmployee(null);
    } finally {
      setSavingCategories(false);
    }
  };

  const handleToggleNotifications = async (employee: EmployeeRecord) => {
    if (!employee.email) return;
    setPanelLoading(true);
    try {
      const next = !employee.notificationsEnabled;
      const result = await rsAuth.updateEmployeeNotifications(employee.email, next);
      if (result.success) {
        setEmployees((prev) => prev.map((entry) => entry.id === employee.id ? { ...entry, notificationsEnabled: next } : entry));
      }
    } finally {
      setPanelLoading(false);
    }
  };

  const handleAddStaff = async () => {
    setStaffExistsInlineMessage('');
    if (!newStaffFirstName.trim() || !newStaffLastName.trim() || !newStaffEmail.trim()) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newStaffEmail.trim())) return;

    setIsSavingStaff(true);
    try {
      const dataService = DataServiceFactory.getService();
      const normalizedEmail = newStaffEmail.trim().toLowerCase();
      const normalizedName = `${newStaffFirstName} ${newStaffLastName}`.trim().toLowerCase();

      const existingLocalByEmail = employees.some((employee) =>
        (employee.email || '').trim().toLowerCase() === normalizedEmail
      );
      const existingLocalByNameNoEmail = employees.some((employee) =>
        (employee.name || '').trim().toLowerCase() === normalizedName && !(employee.email || '').trim()
      );

      if (existingLocalByEmail || existingLocalByNameNoEmail) {
        setStaffExistsInlineMessage(tr('pages.EmployeeManagement.staffMemberAlreadyExistsCheckExisting'));
        return;
      }

      const existingStaff = await dataService.staff.getAllStaffMembers();
      const duplicateByEmail = existingStaff.find((staffMember) => {
        const staffEmail = (staffMember.email || '').trim().toLowerCase();
        return staffEmail !== '' && staffEmail === normalizedEmail;
      });
      const duplicateByNameNoEmail = existingStaff.find((staffMember) => {
        const staffEmail = (staffMember.email || '').trim().toLowerCase();
        const staffName = (staffMember.name || '').trim().toLowerCase();
        return staffEmail === '' && staffName !== '' && staffName === normalizedName;
      });

      if (duplicateByEmail || duplicateByNameNoEmail) {
        setStaffExistsInlineMessage(tr('pages.EmployeeManagement.staffMemberAlreadyExistsInWix'));
        return;
      }

      const newStaff = await dataService.staff.createStaffMember({
        name: `${newStaffFirstName} ${newStaffLastName}`.trim(),
        email: newStaffEmail.trim(),
        phone: newStaffPhone.trim() || '',
        resourceId: '',
      });

      if (newStaff) {
        await syncStaffCache();
        setNewStaffFirstName('');
        setNewStaffLastName('');
        setNewStaffEmail('');
        setNewStaffPhone('');
        setStaffExistsInlineMessage('');
      }
    } catch (error) {
      console.error('Error creating staff member:', error);
    } finally {
      setIsSavingStaff(false);
    }
  };

  const handleDeleteStaff = async () => {
    if (!deleteStaff?.staffId) return;
    const staffId = deleteStaff.staffId;
    setIsDeletingStaff(true);
    setPanelLoading(true);
    try {
      const service = DataServiceFactory.getService();
      await service.staff.deleteStaffMember(staffId);
      await syncStaffCache();
      setInviteSuccess(tr('pages.EmployeeManagement.staffMemberDeleted'));
    } catch (error) {
      console.error('Error deleting staff member:', error);
      setInviteError(tr('pages.EmployeeManagement.couldNotDeleteTheStaffMember'));
    } finally {
      setIsDeletingStaff(false);
      setDeleteStaff(null);
      setPanelLoading(false);
    }
  };

  const handleSaveRole = async (employee: EmployeeRecord) => {
    const nextRole = roleEdits[employee.id] || employee.role;
    if (!employee.staffId || !nextRole || nextRole === employee.role) return;

    setSavingRoleId(employee.id);
    try {
      const result = await rsAuth.updateEmployeeNotifications(
        employee.email || '',
        employee.notificationsEnabled,
        nextRole,
        employee.staffId
      );
      if (!result.success) throw new Error(result.message || tr('pages.EmployeeManagement.failedToUpdateRoleInFirebase'));

      forceStaffReloadRef.current = true;
      const freshEmployees = await listEmployees();
      setEmployees(freshEmployees);
      setRoleEdits(Object.fromEntries(freshEmployees.map((e) => [e.id, e.role])));
    } catch (error) {
      console.error('Failed to save role in Firebase:', error);
    } finally {
      setSavingRoleId(null);
    }
  };

  const handleRevokeCode = async () => {
    if (!deleteCode) return;
    setPanelLoading(true);
    try {
      const result = await rsAuth.revokeRoleCode(deleteCode);
      if (result.success) {
        setCodes((prev) => prev.filter((entry) => entry.code !== deleteCode));
      }
    } finally {
      setDeleteCode(null);
      setPanelLoading(false);
    }
  };

  const handleCreateResource = async () => {
    if (!newResourceName.trim()) return;
    setIsSavingResource(true);
    try {
      const service = DataServiceFactory.getService();
      await service.resources.createResource({
        name: newResourceName.trim(),
        ...(targetCategory ? { typeId: targetCategory.id } : {}),
      });
      setResources(await listResources());
      setNewResourceName('');
      setTargetCategory(null);
    } finally {
      setIsSavingResource(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    setIsCreatingCategory(true);
    try {
      const service = DataServiceFactory.getService();
      await service.resources.createResourceType(newCategoryName.trim());
      setNewCategoryName('');
      const [freshResources, freshResourceTypes] = await Promise.all([listResources(), listResourceTypes()]);
      setResources(freshResources);
      setResourceTypes(freshResourceTypes);
    } finally {
      setIsCreatingCategory(false);
    }
  };

  const handleRenameCategory = async () => {
    if (!renamingCategory || !renameCategoryValue.trim()) return;
    setIsSavingCategoryRename(true);
    try {
      const service = DataServiceFactory.getService();
      const current = resourceTypes.find((t) => t._id === renamingCategory.id);
      await service.resources.updateResourceType(renamingCategory.id, renameCategoryValue.trim(), current?.revision);
      setRenamingCategory(null);
      const [freshResources, freshResourceTypes] = await Promise.all([listResources(), listResourceTypes()]);
      setResources(freshResources);
      setResourceTypes(freshResourceTypes);
    } finally {
      setIsSavingCategoryRename(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deleteCategory) return;
    setPanelLoading(true);
    try {
      const service = DataServiceFactory.getService();
      await service.resources.deleteResourceType(deleteCategory.id);
      const [freshResources, freshResourceTypes] = await Promise.all([listResources(), listResourceTypes()]);
      setResources(freshResources);
      setResourceTypes(freshResourceTypes);
    } finally {
      setDeleteCategory(null);
      setPanelLoading(false);
    }
  };

  const handleDeleteResource = async () => {
    if (!deleteResource?._id) return;
    setPanelLoading(true);
    try {
      const service = DataServiceFactory.getService();
      await service.resources.deleteResource(deleteResource._id);
      setResources(await listResources());
    } finally {
      setDeleteResource(null);
      setPanelLoading(false);
    }
  };

  // Matches Wix's own "Create Resource" dialog: Availability is just
  // 24/7 vs. Default business hours, not a custom per-resource schedule.
  // "24/7" clears workingHoursSchedules entirely; "Default business hours"
  // points it at the business's own shared schedule — no new schedule or
  // day-by-day events need to be created for the resource itself.
  const handleToggleResourceAvailability = async (resource: ResourceRecord, use247: boolean) => {
    setSavingAvailabilityFor(resource._id);
    try {
      const service = DataServiceFactory.getService();
      await service.resources.updateResource(resource._id, {
        revision: resource.revision,
        workingHoursSchedules: use247
          ? { values: [] }
          : { values: [{ scheduleId: businessScheduleIdRef.current ?? undefined, shared: true }] },
      });
      setResources(await listResources());
    } finally {
      setSavingAvailabilityFor(null);
    }
  };

  // Location, same as Availability above: a plain single-select against the
  // business's real locations (Wix's own dialog has no multi-location picker
  // at this level either) — null means "All locations".
  const handleSetResourceLocation = async (resource: ResourceRecord, locationId: string | null) => {
    const service = DataServiceFactory.getService();
    await service.resources.updateResource(resource._id, {
      revision: resource.revision,
      locationOptions: locationId
        ? { availableInAllLocations: false, specificLocationOptions: { availableInBusinessLocations: true, businessLocations: [{ locationId }] } }
        : { availableInAllLocations: true },
    });
    setResources(await listResources());
  };

  const activeEmployees = employees.length;
  const activeCodes = codes.filter((entry) => entry.expiresAt.getTime() > new Date().getTime()).length;
  const enabledNotifications = employees.filter((entry) => entry.notificationsEnabled).length;

  // Wix Bookings auto-creates an uncategorized Resource entry for every
  // staff member (for scheduling) — these land specifically in "Unassigned"
  // as confusing duplicates of people already listed above in the staff
  // sections. Real resources that happen to share a staff member's name
  // (e.g. equipment assigned per-person) are NOT duplicates — they're
  // deliberately filed under a real category, so only filter Unassigned.
  const staffNames = new Set(employees.map((e) => e.name.trim().toLowerCase()).filter(Boolean));
  const displayResources: GroupedResources = Object.fromEntries(
    Object.entries(resources)
      .map(([category, items]) => [
        category,
        category === 'Unassigned'
          ? items.filter((r) => !staffNames.has((r.name || '').trim().toLowerCase()))
          : items,
      ] as [string, ResourceRecord[]])
      .filter(([, items]) => items.length > 0)
  );

  return (
    <IonLabel className={styles.modalWrapper}>
    <BasePageWithHeader
      ref={baseHeaderRef}
      title={tr('pages.EmployeeManagement.employeeManagement')}
      toolbarClassName={styles.toolbar}
      smallTitleClassName={styles.smallTitle}
      titleHeaderClassName={styles.titleHeader}
      startButtons={
        <IonButton className={styles.backButton} onClick={onClose} shape="round" aria-label={tr('pages.EmployeeManagement.close')}>
          <IonIcon icon={chevronBack} />
        </IonButton>
      }
      endButtons={
        <IonButton shape="round" className={styles.refreshButton} onClick={refreshAll}>
          <IonIcon icon={refreshOutline} />
        </IonButton>
      }
    >
      <IonContent scrollY={false} className={styles.content}>
        <IonGrid
          ref={containerRef}
          className={styles.container}
          onScroll={(e) => handleScroll((e.target as HTMLElement).scrollTop || 0)}
        >
          <TitleCard
            ref={titleCardAnimationRef}
            title={tr('pages.EmployeeManagement.employeeManagement')}
            subtitle={tr('pages.EmployeeManagement.teamAccessRolesResources')}
            subtitleClassName={styles.heroSubtitle}
            cardClassName={styles.hero}
            titleContainerClassName={styles.titleContainer}
            titleClassName={styles.heroTitle}
          />

          {/* Stats row */}
          <IonLabel className={styles.statsRow}>
            <IonCard className={styles.statCard}>
              <IonIcon icon={peopleOutline} className={styles.statIcon} />
              <span className={styles.statValue}>{loading ? '–' : activeEmployees}</span>
              <span className={styles.statLabel}>{tr('pages.EmployeeManagement.teamMembers')}</span>
            </IonCard>
            <IonCard className={styles.statCard}>
              <IonIcon icon={keyOutline} className={styles.statIcon} />
              <span className={styles.statValue}>{loading ? '–' : activeCodes}</span>
              <span className={styles.statLabel}>{tr('pages.EmployeeManagement.activeCodes')}</span>
            </IonCard>
            <IonCard className={styles.statCard}>
              <IonIcon icon={notificationsOutline} className={styles.statIcon} />
              <span className={styles.statValue}>{loading ? '–' : enabledNotifications}</span>
              <span className={styles.statLabel}>{tr('pages.EmployeeManagement.notificationsOn')}</span>
            </IonCard>
          </IonLabel>

          {/* Phone Agents — links out to the existing Phone Agents modal
              instead of duplicating agent management here. */}
          {onOpenPhoneAgents && (
            <button type="button" className={styles.phoneAgentsRow} onClick={onOpenPhoneAgents}>
              <div className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                <IonIcon icon={callOutline} />
              </div>
              <IonLabel className={styles.phoneAgentsText}>
                <span className={styles.phoneAgentsTitle}>{tr('pages.EmployeeManagement.phoneAgents')}</span>
                <span className={styles.phoneAgentsSubtitle}>{tr('pages.EmployeeManagement.manageWhoAnswersIncomingCalls')}</span>
              </IonLabel>
              <IonIcon icon={chevronForwardOutline} className={styles.chevronIcon} />
            </button>
          )}

          {/* Panels render immediately, even while loading/empty — this
              screen remounts fresh (no cache) every time it opens, so
              gating on `loading` hid every panel behind a spinner on every
              single open, not just a genuine first load. Individual
              sections already show their own empty-state text
              (noStaffRecordsFound, etc.) once loading settles. */}
          {/* Invite Employee */}
          <IonCard className={styles.panel}>
            <button type="button" className={styles.panelHeader} onClick={() => toggleSection('invite')}>
              <div className={`${styles.iconBadge} ${styles.iconBadgeSecondary} ${styles.panelIconBadge}`}>
                <IonIcon icon={mailOutline} />
              </div>
              <span className={styles.panelTitle}>{tr('pages.EmployeeManagement.inviteEmployee')}</span>
              <IonIcon icon={chevronDownOutline} className={`${styles.sectionChevron} ${collapsedSections.invite ? '' : styles.sectionChevronOpen}`} />
            </button>
            {!collapsedSections.invite && (
              <IonCardContent className={styles.panelBody}>
                <IonLabel className={styles.fieldBox}>
                  <input
                    type="email"
                    value={inviteEmail}
                    placeholder="employee@email.com"
                    className={styles.fieldInput}
                    onChange={(e) => setInviteEmail(e.target.value)}
                  />
                </IonLabel>
                <SimpleSelect
                  value={inviteRole}
                  onChange={setInviteRole}
                  options={assignableRoleOptions}
                  placeholder={tr('pages.EmployeeManagement.role')}
                  cancelLabel="Cancel"
                  onCancel={() => { setInviteRole(''); setInviteEmail(''); }}
                />
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={handleCreateInvite}
                  disabled={isInviting || !inviteEmail.trim() || !inviteRole}
                >
                  {isInviting
                    ? <IonSpinner name="bubbles" color="light" className={styles.btnSpinner} />
                    : tr('pages.EmployeeManagement.sendInvite')}
                </button>
              </IonCardContent>
            )}
          </IonCard>

          {/* Active Invitation Codes */}
          <IonCard className={styles.panel}>
            <button type="button" className={styles.panelHeader} onClick={() => toggleSection('codes')}>
              <div className={`${styles.iconBadge} ${styles.iconBadgePrimary} ${styles.panelIconBadge}`}>
                <IonIcon icon={keyOutline} />
              </div>
              <span className={styles.panelTitle}>{tr('pages.EmployeeManagement.activeInvitationCodes')}</span>
              <IonIcon icon={chevronDownOutline} className={`${styles.sectionChevron} ${collapsedSections.codes ? '' : styles.sectionChevronOpen}`} />
            </button>
            {!collapsedSections.codes && (
              <IonCardContent className={styles.panelBody}>
                {codes.length === 0 ? (
                  <p className={styles.emptyHint}>{tr('pages.EmployeeManagement.noActiveInvitationCodes')}</p>
                ) : codes.map((code) => (
                  <IonLabel key={code.code} className={styles.listRow}>
                    <IonLabel className={styles.listRowText}>
                      <h3>{code.email}</h3>
                      <p>{tr('pages.EmployeeManagement.code', { code: code.code })} {formatExpiry(code.expiresAt)}</p>
                    </IonLabel>
                    <span className={`${styles.roleBadge} ${getRoleTone(code.role)}`}>{code.role}</span>
                    <button type="button" className={styles.deleteIconBtn} onClick={() => setDeleteCode(code.code)}>
                      <IonIcon icon={trashOutline} />
                    </button>
                  </IonLabel>
                ))}
              </IonCardContent>
            )}
          </IonCard>

          {/* Employee Notification Access */}
          <IonCard className={styles.panel}>
            <button type="button" className={styles.panelHeader} onClick={() => toggleSection('notifications')}>
              <div className={`${styles.iconBadge} ${styles.iconBadgeSecondary} ${styles.panelIconBadge}`}>
                <IonIcon icon={notificationsOutline} />
              </div>
              <span className={styles.panelTitle}>{tr('pages.EmployeeManagement.employeeNotificationAccess')}</span>
              <IonIcon icon={chevronDownOutline} className={`${styles.sectionChevron} ${collapsedSections.notifications ? '' : styles.sectionChevronOpen}`} />
            </button>
            {!collapsedSections.notifications && (
              <IonCardContent className={styles.panelBody}>
                {employees.length === 0 ? (
                  <p className={styles.emptyHint}>{tr('pages.EmployeeManagement.noStaffRecordsFound')}</p>
                ) : employees.map((employee) => (
                  <IonLabel key={employee.id} className={styles.listRow}>
                    <IonLabel className={styles.listRowText}>
                      <h3>{employee.name}</h3>
                      <p>{employee.email || tr('pages.EmployeeManagement.noEmail')} · {employee.role}</p>
                    </IonLabel>
                    {employee.notificationsEnabled && employee.email && (
                      <button
                        type="button"
                        className={styles.categoriesBtn}
                        onClick={() => openCategoriesModal(employee)}
                      >{tr('pages.EmployeeManagement.categories')}
                      </button>
                    )}
                    <label className={styles.toggleWrap}>
                      <input
                        type="checkbox"
                        className={styles.toggleInput}
                        checked={employee.notificationsEnabled}
                        disabled={panelLoading || !employee.email}
                        onChange={() => handleToggleNotifications(employee)}
                      />
                      <span className={`${styles.toggleTrack} ${employee.notificationsEnabled ? styles.toggleTrackOn : ''}`}>
                        <span className={styles.toggleThumb} />
                      </span>
                    </label>
                  </IonLabel>
                ))}
              </IonCardContent>
            )}
          </IonCard>

          {/* Manage Staff Roles */}
          <IonCard className={styles.panel}>
            <button type="button" className={styles.panelHeader} onClick={() => toggleSection('roles')}>
              <div className={`${styles.iconBadge} ${styles.iconBadgeWarning} ${styles.panelIconBadge}`}>
                <IonIcon icon={shieldCheckmarkOutline} />
              </div>
              <span className={styles.panelTitle}>{tr('pages.EmployeeManagement.manageStaffRoles')}</span>
              <IonIcon icon={chevronDownOutline} className={`${styles.sectionChevron} ${collapsedSections.roles ? '' : styles.sectionChevronOpen}`} />
            </button>
            {!collapsedSections.roles && (
              <IonCardContent className={styles.panelBody}>
                {employees.length === 0 ? (
                  <p className={styles.emptyHint}>{tr('pages.EmployeeManagement.noStaffRecordsFound')}</p>
                ) : employees.map((employee) => {
                  const selectedRole = roleEdits[employee.id] ?? employee.role;
                  const hasChanged = selectedRole !== employee.role;
                  // An account this person may not manage (e.g. a manager looking at an owner) shows its role but cannot be changed
                  const canEditRole = canManageAccount(user?.role, employee.role);
                  const rowRoleOptions = roleOptions.filter(o => o.value === employee.role || (canEditRole && canAssignRole(user?.role, o.value)));
                  return (
                    <IonLabel key={`${employee.id}-role`} className={styles.roleRow}>
                      <IonLabel className={styles.listRowText}>
                        <h3>{employee.name}</h3>
                        <p>{employee.email || tr('pages.EmployeeManagement.noEmail')}</p>
                      </IonLabel>
                      <IonLabel className={styles.roleRowControls}>
                        <SimpleSelect
                          value={selectedRole}
                          onChange={(v) => setRoleEdits((prev) => ({ ...prev, [employee.id]: v }))}
                          options={rowRoleOptions}
                          className={styles.roleSelectInline}
                        />
                        <button
                          type="button"
                          className={styles.saveRoleBtn}
                          disabled={!canEditRole || !hasChanged || !employee.staffId || savingRoleId === employee.id}
                          onClick={() => handleSaveRole(employee)}
                        >
                          {savingRoleId === employee.id ? '...' : tr('pages.EmployeeManagement.save')}
                        </button>
                        <button
                          type="button"
                          className={styles.deleteIconBtn}
                          disabled={!employee.staffId}
                          onClick={() => setDeleteStaff(employee)}
                        >
                          <IonIcon icon={trashOutline} />
                        </button>
                      </IonLabel>
                    </IonLabel>
                  );
                })}
              </IonCardContent>
            )}
          </IonCard>

          {/* Add Staff Member */}
          <IonCard className={styles.panel}>
            <button type="button" className={styles.panelHeader} onClick={() => toggleSection('addStaff')}>
              <div className={`${styles.iconBadge} ${styles.iconBadgeTertiary} ${styles.panelIconBadge}`}>
                <IonIcon icon={addOutline} />
              </div>
              <span className={styles.panelTitle}>{tr('pages.EmployeeManagement.addStaffMember')}</span>
              <IonIcon icon={chevronDownOutline} className={`${styles.sectionChevron} ${collapsedSections.addStaff ? '' : styles.sectionChevronOpen}`} />
            </button>
            {!collapsedSections.addStaff && (
              <IonCardContent className={styles.panelBody}>
                <IonLabel className={styles.fieldRowTwo}>
                  <IonLabel className={styles.fieldBox}>
                    <input type="text" value={newStaffFirstName} placeholder={tr('pages.EmployeeManagement.firstName')} className={styles.fieldInput}
                      onChange={(e) => { setNewStaffFirstName(e.target.value); setStaffExistsInlineMessage(''); }} />
                  </IonLabel>
                  <IonLabel className={styles.fieldBox}>
                    <input type="text" value={newStaffLastName} placeholder={tr('pages.EmployeeManagement.lastName')} className={styles.fieldInput}
                      onChange={(e) => { setNewStaffLastName(e.target.value); setStaffExistsInlineMessage(''); }} />
                  </IonLabel>
                </IonLabel>
                <IonLabel className={styles.fieldBox}>
                  <input type="email" value={newStaffEmail} placeholder={tr('pages.EmployeeManagement.email')} className={styles.fieldInput}
                    onChange={(e) => { setNewStaffEmail(e.target.value); setStaffExistsInlineMessage(''); }} />
                </IonLabel>
                <IonLabel className={styles.fieldBox}>
                  <input type="tel" value={newStaffPhone} placeholder={tr('pages.EmployeeManagement.phoneOptional')} className={styles.fieldInput}
                    onChange={(e) => { setNewStaffPhone(e.target.value); setStaffExistsInlineMessage(''); }} />
                </IonLabel>
                {staffExistsInlineMessage && <p className={styles.errorHint}>{staffExistsInlineMessage}</p>}
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={handleAddStaff}
                  disabled={isSavingStaff || !newStaffFirstName.trim() || !newStaffLastName.trim() || !newStaffEmail.trim()}
                >
                  {isSavingStaff
                    ? <IonSpinner name="bubbles" color="light" className={styles.btnSpinner} />
                    : tr('pages.EmployeeManagement.createStaffMember')}
                </button>
              </IonCardContent>
            )}
          </IonCard>

          {/* Resources */}
          <IonCard className={styles.panel}>
            <button type="button" className={styles.panelHeader} onClick={() => toggleSection('resources')}>
              <div className={`${styles.iconBadge} ${styles.iconBadgePrimary} ${styles.panelIconBadge}`}>
                <IonIcon icon={keyOutline} />
              </div>
              <span className={styles.panelTitle}>{tr('pages.EmployeeManagement.resources')}</span>
              <IonIcon icon={chevronDownOutline} className={`${styles.sectionChevron} ${collapsedSections.resources ? '' : styles.sectionChevronOpen}`} />
            </button>
            {!collapsedSections.resources && (
              <IonCardContent className={styles.panelBody}>
                <IonLabel className={styles.fieldRowTwo}>
                  <IonLabel className={styles.fieldBox}>
                    <input type="text" value={newCategoryName} placeholder={tr('pages.EmployeeManagement.newCategoryName')} className={styles.fieldInput}
                      onChange={(e) => setNewCategoryName(e.target.value)} />
                  </IonLabel>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={handleCreateCategory}
                    disabled={isCreatingCategory || !newCategoryName.trim()}
                  >
                    {isCreatingCategory
                      ? <IonSpinner name="bubbles" color="primary" className={styles.btnSpinnerSmall} />
                      : tr('pages.EmployeeManagement.createCategory')}
                  </button>
                </IonLabel>

                {Object.keys(displayResources).length === 0 ? (
                  <p className={styles.emptyHint}>{tr('pages.EmployeeManagement.noResourcesCreatedYet')}</p>
                ) : Object.entries(displayResources).map(([category, items]) => {
                  const categoryType = resourceTypes.find((t) => t.name === category);
                  const isRenamingThis = !!categoryType && renamingCategory?.id === categoryType._id;
                  const isAddingHere = !!categoryType && targetCategory?.id === categoryType._id;
                  return (
                    <IonLabel key={category} className={styles.resourceGroup}>
                      <IonLabel className={styles.resourceGroupHeader}>
                        {isRenamingThis ? (
                          <>
                            <input
                              type="text"
                              value={renameCategoryValue}
                              className={styles.categoryRenameInput}
                              onChange={(e) => setRenameCategoryValue(e.target.value)}
                              autoFocus
                            />
                            <button
                              type="button"
                              className={styles.categoryRenameSave}
                              onClick={handleRenameCategory}
                              disabled={isSavingCategoryRename || !renameCategoryValue.trim()}
                            >{tr('pages.EmployeeManagement.save')}
                            </button>
                            <button type="button" className={styles.categoryRenameCancel} onClick={() => setRenamingCategory(null)}>{tr('pages.EmployeeManagement.cancel')}
                            </button>
                          </>
                        ) : (
                          <>
                            <span>{translateDynamic(category)}</span>
                            <IonBadge className={styles.resourceGroupCount}>{items.length}</IonBadge>
                            {categoryType && (
                              <CategoryMenu
                                onAddResource={() => setTargetCategory({ id: categoryType._id, name: categoryType.name })}
                                onRename={() => {
                                  setRenamingCategory({ id: categoryType._id, name: categoryType.name });
                                  setRenameCategoryValue(categoryType.name);
                                }}
                                onDelete={() => setDeleteCategory({ id: categoryType._id, name: categoryType.name })}
                              />
                            )}
                          </>
                        )}
                      </IonLabel>

                      {isAddingHere && (
                        <IonLabel className={styles.addResourceInline}>
                          <IonLabel className={styles.fieldBox}>
                            <input type="text" value={newResourceName} placeholder={tr('pages.EmployeeManagement.resourceName')} className={styles.fieldInput}
                              onChange={(e) => setNewResourceName(e.target.value)} autoFocus />
                          </IonLabel>
                          <IonLabel className={styles.addResourceInlineActions}>
                            <button
                              type="button"
                              className={styles.categoryRenameCancel}
                              onClick={() => { setTargetCategory(null); setNewResourceName(''); }}
                            >{tr('pages.EmployeeManagement.cancel')}
                            </button>
                            <button
                              type="button"
                              className={styles.categoryRenameSave}
                              onClick={handleCreateResource}
                              disabled={isSavingResource || !newResourceName.trim()}
                            >
                              {isSavingResource
                                ? <IonSpinner name="bubbles" color="light" className={styles.btnSpinnerSmall} />
                                : tr('pages.EmployeeManagement.addResource')}
                            </button>
                          </IonLabel>
                        </IonLabel>
                      )}

                      {items.map((resource) => {
                        const is247 = resourceIs247(resource);
                        const savingThis = savingAvailabilityFor === resource._id;
                        return (
                          <IonLabel key={resource._id} className={styles.listRow}>
                            <IonLabel className={styles.listRowText}>
                              <h3>{resource.name}</h3>
                              <IonLabel className={styles.availabilityToggle}>
                                <button
                                  type="button"
                                  className={`${styles.availabilityToggleBtn} ${is247 ? styles.availabilityToggleBtnActive : ''}`}
                                  disabled={savingThis}
                                  onClick={() => handleToggleResourceAvailability(resource, true)}
                                >
                                  24/7
                                </button>
                                <button
                                  type="button"
                                  className={`${styles.availabilityToggleBtn} ${!is247 ? styles.availabilityToggleBtnActive : ''}`}
                                  disabled={savingThis}
                                  onClick={() => handleToggleResourceAvailability(resource, false)}
                                >{tr('pages.EmployeeManagement.defaultBusinessHours')}
                                </button>
                              </IonLabel>
                              <SimpleSelect
                                value={resourceLocationIds(resource)[0] ?? 'all'}
                                onChange={(v) => handleSetResourceLocation(resource, v === 'all' ? null : v)}
                                options={[{ value: 'all', label: tr('pages.EmployeeManagement.allLocations') }, ...businessLocations.map((l) => ({ value: l._id, label: l.name }))]}
                                className={styles.locationSelectInline}
                              />
                            </IonLabel>
                            <button type="button" className={styles.deleteIconBtn} onClick={() => setDeleteResource(resource)}>
                              <IonIcon icon={trashOutline} />
                            </button>
                          </IonLabel>
                        );
                      })}
                    </IonLabel>
                  );
                })}
              </IonCardContent>
            )}
          </IonCard>
        </IonGrid>
      </IonContent>

      {/* Success/update at the bottom; errors at the top, where they are noticed first */}
      <IonToast isOpen={!!inviteSuccess} message={inviteSuccess} duration={3500} position="bottom" cssClass={TOAST_CLASS_OK} onDidDismiss={() => setInviteSuccess('')} />
      <IonToast isOpen={!!inviteError} message={inviteError} duration={5000} position="top" cssClass={TOAST_CLASS_ERROR} onDidDismiss={() => setInviteError('')} />

      <IonAlert
        isOpen={!!deleteCode}
        cssClass="glass-alert"
        onDidDismiss={() => setDeleteCode(null)}
        header={tr('pages.EmployeeManagement.revokeInvitation')}
        message={tr('pages.EmployeeManagement.removeThisEmployeeInviteCode')}
        buttons={[
          { text: tr('pages.EmployeeManagement.cancel'), role: 'cancel' },
          { text: tr('pages.EmployeeManagement.revoke'), role: 'destructive', handler: handleRevokeCode },
        ]}
      />

      <IonAlert
        isOpen={!!deleteStaff}
        cssClass="glass-alert"
        backdropDismiss={!isDeletingStaff}
        onDidDismiss={() => { if (!isDeletingStaff) setDeleteStaff(null); }}
        header={tr('pages.EmployeeManagement.deleteStaffMember')}
        message={tr('pages.EmployeeManagement.delete2', { v1: deleteStaff?.name || 'this staff member' })}
        buttons={[
          { text: tr('pages.EmployeeManagement.cancel'), role: 'cancel', htmlAttributes: { disabled: isDeletingStaff } },
          {
            // The spinner lives in the button itself: the alert stays open (handler returns false) until the delete is finished
            text: isDeletingStaff ? tr('pages.EmployeeManagement.deleting') : tr('pages.EmployeeManagement.delete'),
            role: 'destructive',
            cssClass: isDeletingStaff ? 'alert-button-loading' : '', // i18n-ignore: CSS class name
            handler: () => { if (!isDeletingStaff) void handleDeleteStaff(); return false; },
          },
        ]}
      />

      <IonAlert
        isOpen={!!deleteResource}
        cssClass="glass-alert"
        onDidDismiss={() => setDeleteResource(null)}
        header={tr('pages.EmployeeManagement.deleteResource')}
        message={tr('pages.EmployeeManagement.delete2', { v1: deleteResource?.name || 'this resource' })}
        buttons={[
          { text: tr('pages.EmployeeManagement.cancel'), role: 'cancel' },
          { text: tr('pages.EmployeeManagement.delete'), role: 'destructive', handler: handleDeleteResource },
        ]}
      />

      <GlassConfirmSheet
        isOpen={!!deleteCategory}
        title={tr('pages.EmployeeManagement.deleteCategory')}
        message={tr('pages.EmployeeManagement.deleteItsResourcesBecomeUncategorizedNot', { v1: deleteCategory?.name || 'this category' })}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDeleteCategory}
        onDismiss={() => setDeleteCategory(null)}
      />

      <SheetModal
        isOpen={!!categoriesEmployee}
        onClose={() => setCategoriesEmployee(null)}
        title={tr('pages.EmployeeManagement.notificationCategories')}
        menuOptions={[]}
        additionalContent={
          <>
            <IonList lines="none">
              {([
                { value: 'bookings', label: tr('pages.EmployeeManagement.bookings'), sublabel: 'New bookings, cancellations, reschedules' },
                { value: 'payments', label: tr('pages.EmployeeManagement.payments'), sublabel: 'Payment received and failed' },
                { value: 'team', label: tr('pages.EmployeeManagement.team'), sublabel: 'Team updates and announcements' },
                { value: 'forms', label: tr('pages.EmployeeManagement.forms'), sublabel: 'New form submissions' },
                { value: 'overdue', label: tr('pages.EmployeeManagement.overdue'), sublabel: 'Overdue invoice reminders' },
                { value: 'invoice', label: tr('pages.EmployeeManagement.invoiceSent'), sublabel: 'Automatic invoice confirmations' },
              ] as const).map(opt => (
                <IonItem key={opt.value} lines="none" className={styles.categoryCheckRow}>
                  <IonLabel>
                    <h3>{opt.label}</h3>
                    <p>{opt.sublabel}</p>
                  </IonLabel>
                  <IonCheckbox
                    slot="end"
                    checked={draftCategories[opt.value as keyof NotificationCategories]}
                    onIonChange={() => setDraftCategories(prev => ({ ...prev, [opt.value]: !prev[opt.value as keyof NotificationCategories] }))}
                  />
                </IonItem>
              ))}
            </IonList>
            <div className={styles.categoryCheckFooter}>
              <button type="button" className={styles.categoryCheckCancelBtn} onClick={() => setCategoriesEmployee(null)}>
                {tr('components.GlassPicker.cancel')}
              </button>
              <button type="button" className={styles.categoryCheckSaveBtn} onClick={handleSaveCategories}>
                {tr('components.GlassPicker.save')}
              </button>
            </div>
          </>
        }
      />
    </BasePageWithHeader>
    </IonLabel>
  );
};

export default EmployeeManagement;
